// src/pages/markets/board/state/useProfileBar.ts
//
// The profile lifecycle: resolving a pointer, deciding dirty, applying a
// profile onto the board, and the two confirmation dialogs. Extracted so
// ScreenerBoardView is about the board and this is about profiles.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccessFeatures } from '../../../../auth/useAccessFeatures';
import {
    BOARD_COLUMNS,
    type BodyMode,
    type CardView,
} from '../board/columnConfig';
import { defaultFilters, sortOptions } from '../api/useSchema';
import { gatedNamer, resolveSlotCategory } from '../board/categories';
import type { BoardFilters, BoardSchema } from '../api/boardTypes';
import { orderByIds } from './profileOrder';
import { columnsAreEqual, columnsFromBoard } from './profileSerde';
import { useBoardProfiles } from './useBoardProfiles';
import { applyPreference, type StoredColumn } from './usePreferences';
import type { BoardProfile, ProfileRef } from './profileTypes';

export interface ColumnLayout {
    body: BodyMode;
    view: CardView;
}

export interface UseProfileBarArgs {
    schema: BoardSchema | undefined;
    filters: Record<string, BoardFilters> | null;
    layout: Record<string, ColumnLayout> | null;
    sources: Record<string, string> | null;
    offered: (group: string) => boolean;
    setFilters: (next: Record<string, BoardFilters>) => void;
    setLayout: (next: Record<string, ColumnLayout>) => void;
    setSources: (next: Record<string, string>) => void;
    saveFilters: (column: string, filters: BoardFilters) => void;
    saveLayout: (
        column: string,
        patch: { body?: BodyMode; view?: CardView }
    ) => void;
    saveSource: (column: string, source: string) => void;
    activeProfile: ProfileRef | null;
    setActiveProfile: (ref: ProfileRef | null) => void;
    profileOrder: readonly string[];
    setProfileOrder: (ids: string[]) => void;
}

export function useProfileBar({
    schema,
    filters,
    layout,
    sources,
    offered,
    setFilters,
    setLayout,
    setSources,
    saveFilters,
    saveLayout,
    saveSource,
    activeProfile,
    setActiveProfile,
    profileOrder,
    setProfileOrder,
}: UseProfileBarArgs) {
    const profiles = useBoardProfiles(profileOrder);
    const access = useAccessFeatures();
    const isAdmin = access.role === 'admin';

    const [pendingDelete, setPendingDelete] = useState<BoardProfile | null>(
        null
    );
    const [pendingPromote, setPendingPromote] = useState<BoardProfile | null>(
        null
    );
    // An admin's dragged shared order, held until Save. Personal order never
    // waits: it is one reader's own arrangement.
    const [pendingSharedOrder, setPendingSharedOrder] = useState<
        string[] | null
    >(null);
    // The personal drop is already on its way to Firestore; this is only what the
    // row shows until the store echoes it back a tick later.
    const [droppedPersonalOrder, setDroppedPersonalOrder] = useState<
        string[] | null
    >(null);

    const order = useMemo(() => BOARD_COLUMNS.map(c => c.id), []);

    // Both rows render the drag's result immediately: a list that reorders a tick
    // late shows a frame of the old order, and the drop animation lands wrong.
    const sharedRow = useMemo(
        () =>
            pendingSharedOrder
                ? orderByIds(profiles.shared, pendingSharedOrder)
                : profiles.shared,
        [profiles.shared, pendingSharedOrder]
    );
    const personalRow = useMemo(
        () =>
            droppedPersonalOrder
                ? orderByIds(profiles.personal, droppedPersonalOrder)
                : profiles.personal,
        [profiles.personal, droppedPersonalOrder]
    );

    // Let go of the local copy once the store agrees, so a change made elsewhere
    // is not outranked by a drag from earlier in this session.
    useEffect(() => {
        if (!droppedPersonalOrder) return;
        const live = profiles.personal.map(p => p.id);
        if (
            live.length === droppedPersonalOrder.length &&
            live.every((id, i) => id === droppedPersonalOrder[i])
        )
            setDroppedPersonalOrder(null);
    }, [profiles.personal, droppedPersonalOrder]);

    const findProfile = useCallback(
        (ref: ProfileRef | null): BoardProfile | null => {
            if (!ref) return null;
            const pool =
                ref.scope === 'shared' ? profiles.shared : profiles.personal;
            return pool.find(p => p.id === ref.id) ?? null;
        },
        [profiles.shared, profiles.personal]
    );

    const liveColumns = useMemo(
        () =>
            filters && layout && sources
                ? columnsFromBoard(order, filters, layout, sources)
                : null,
        [filters, layout, sources, order]
    );

    const activeProfileDoc = findProfile(activeProfile);
    const dirty =
        activeProfileDoc !== null &&
        liveColumns !== null &&
        !columnsAreEqual(liveColumns, activeProfileDoc.columns);

    // The board state moves first, then the writes go out — a Firestore write
    // fired from inside a state updater runs during render, and React may run an
    // updater more than once.
    const applyProfile = useCallback(
        (profile: BoardProfile) => {
            if (!schema || !filters || !layout || !sources) return;
            const nextFilters = { ...filters };
            const nextLayout = { ...layout };
            const nextSources = { ...sources };
            const writes: Array<() => void> = [];

            for (const col of profile.columns) {
                // A profile may name a column this board no longer renders.
                if (!(col.id in nextFilters) || !(col.id in nextLayout))
                    continue;
                // Resolve first, then seed and clamp against what the pane will
                // actually render: a profile naming a category the schema has
                // dropped must land on the same fallback the board shows.
                const category = resolveSlotCategory(schema, col.id, {
                    storedSource: col.source,
                    storedGroup: (col.filters as Partial<StoredColumn>).group,
                }, gatedNamer(offered));
                const seed = defaultFilters(schema, category.source);
                const applied = applyPreference(
                    seed,
                    col.filters as Partial<StoredColumn>
                );
                // A sort key the column does not offer answers 400, so a profile
                // written against another category falls back to the seed's.
                const sortKeys = sortOptions(schema, category.source);
                const merged = {
                    ...applied,
                    group: category.group,
                    sortBy: sortKeys.includes(applied.sortBy)
                        ? applied.sortBy
                        : seed.sortBy,
                };
                const body = col.layout.body ?? nextLayout[col.id].body;
                const view = col.layout.view ?? nextLayout[col.id].view;
                nextFilters[col.id] = merged;
                nextLayout[col.id] = { body, view };
                nextSources[col.id] = category.source;
                writes.push(() => {
                    saveFilters(col.id, merged);
                    saveLayout(col.id, { body, view });
                    saveSource(col.id, category.source);
                });
            }

            setFilters(nextFilters);
            setLayout(nextLayout);
            setSources(nextSources);
            for (const write of writes) write();
        },
        [
            schema,
            filters,
            layout,
            sources,
            offered,
            setFilters,
            setLayout,
            setSources,
            saveFilters,
            saveLayout,
            saveSource,
        ]
    );

    // A shared profile deleted out from under the reader leaves a pointer that
    // resolves to nothing: without this the chip is unlit but Reset stays enabled
    // and does nothing.
    //
    // Gated on a read that SUCCEEDED, not one that merely settled: isLoaded is
    // true on error too, retries are off, and both lists are empty then — so
    // clearing on that would persist a null pointer for a profile that is still
    // there, losing the user's selection to one network blip.
    useEffect(() => {
        if (!profiles.isSuccess || !activeProfile) return;
        if (!findProfile(activeProfile)) setActiveProfile(null);
    }, [profiles.isSuccess, activeProfile, findProfile, setActiveProfile]);

    const onSelect = useCallback(
        (ref: ProfileRef) => {
            setActiveProfile(ref);
            const profile = findProfile(ref);
            if (profile) applyProfile(profile);
        },
        [applyProfile, findProfile, setActiveProfile]
    );

    // Reset is the only way back from a drag nobody wants to keep.
    const onReset = useCallback(() => {
        setPendingSharedOrder(null);
        if (activeProfileDoc) applyProfile(activeProfileDoc);
    }, [activeProfileDoc, applyProfile]);

    // One press commits both jobs, and neither ever writes a shared profile's
    // contents: the order write touches the order field alone.
    const onSave = useCallback(() => {
        if (activeProfile?.scope === 'user' && liveColumns && dirty) {
            void profiles.saveColumns(activeProfile.id, liveColumns);
        }
        if (!pendingSharedOrder) return;
        // Cleared on the write landing, not on the press: a rejected order has to
        // stay on screen to be retried rather than vanish unexplained.
        void profiles
            .saveSharedOrder(pendingSharedOrder)
            .then(() => setPendingSharedOrder(null))
            .catch(err => {
                console.warn('Could not save the shared profile order', err);
            });
    }, [activeProfile, dirty, liveColumns, pendingSharedOrder, profiles]);

    const onCreate = useCallback(
        (name: string) => {
            if (!liveColumns) return;
            void profiles.create(name, liveColumns).then(setActiveProfile);
        },
        [liveColumns, profiles, setActiveProfile]
    );

    const onRename = useCallback(
        (id: string, name: string) => void profiles.rename(id, name),
        [profiles]
    );

    const onReorderPersonal = useCallback(
        (ids: string[]) => {
            setDroppedPersonalOrder(ids);
            setProfileOrder(ids);
        },
        [setProfileOrder]
    );

    const onReorderShared = useCallback(
        (ids: string[]) => setPendingSharedOrder(ids),
        []
    );

    const confirmDelete = useCallback(() => {
        if (!pendingDelete) return;
        void profiles.remove({
            scope: pendingDelete.scope,
            id: pendingDelete.id,
        });
        if (activeProfile?.id === pendingDelete.id) setActiveProfile(null);
        setPendingDelete(null);
    }, [activeProfile, pendingDelete, profiles, setActiveProfile]);

    const confirmPromote = useCallback(
        (name: string, description: string) => {
            if (pendingPromote)
                void profiles.promote(pendingPromote.id, name, description);
            setPendingPromote(null);
        },
        [pendingPromote, profiles]
    );

    return {
        barProps: {
            shared: sharedRow,
            personal: personalRow,
            active: activeProfile,
            dirty,
            atCap: profiles.atCap,
            isAdmin,
            canOrderShared: isAdmin,
            sharedOrderPending: pendingSharedOrder !== null,
            onReorderPersonal,
            onReorderShared,
            onSelect,
            onReset,
            onSave,
            onCreate,
            onRename,
            onRequestDelete: (ref: ProfileRef) =>
                setPendingDelete(findProfile(ref)),
            onRequestPromote: (id: string) =>
                setPendingPromote(findProfile({ scope: 'user', id })),
        },
        shared: profiles.shared,
        pendingDelete,
        pendingPromote,
        cancelDelete: () => setPendingDelete(null),
        cancelPromote: () => setPendingPromote(null),
        confirmDelete,
        confirmPromote,
    };
}
